/**
 * GA4 E-commerce Tracking System
 * Comprehensive custom event tracking for user journey analysis
 * 
 * This module provides detailed tracking of user interactions throughout
 * the e-commerce journey from initial visit to checkout completion.
 * 
 * @version 1.0.0
 * @author Store Analytics Team
 */

class GA4EcommerceTracker {
  constructor() {
    this.isInitialized = false;
    this.sessionData = {};
    this.userInteractions = [];
    this.init();
  }

  /**
   * Initialize the tracking system
   */
  init() {
    if (typeof gtag === 'undefined') {
      console.warn('GA4 Tracker: gtag not available, retrying in 1 second...');
      setTimeout(() => this.init(), 1000);
      return;
    }

    this.isInitialized = true;
    this.setupSessionTracking();
    this.bindEvents();
    this.trackPageView();
    console.log('GA4 E-commerce Tracker initialized successfully');
  }

  /**
   * Setup session-level tracking data
   */
  setupSessionTracking() {
    this.sessionData = {
      session_start: Date.now(),
      page_views: 0,
      scroll_depth: 0,
      time_on_site: 0,
      device_type: this.getDeviceType(),
      traffic_source: this.getTrafficSource()
    };
  }

  /**
   * Bind event listeners for various user interactions
   */
  bindEvents() {
    // Page engagement tracking
    this.trackScrollDepth();
    this.trackTimeOnPage();
    
    // Product interaction tracking
    this.trackProductViews();
    this.trackProductClicks();
    
    // Cart interaction tracking
    this.trackAddToCart();
    this.trackRemoveFromCart();
    this.trackCartView();
    
    // Search and navigation tracking
    this.trackSearch();
    this.trackNavigation();
    
    // Checkout process tracking
    this.trackCheckoutSteps();
    
    // User engagement tracking
    this.trackUserEngagement();
    
    // Newsletter and form tracking
    this.trackFormInteractions();
  }

  /**
   * Track enhanced page views with custom parameters
   */
  trackPageView() {
    const pageData = {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      content_group1: this.getContentGroup(),
      custom_parameter_1: this.getCustomPageData()
    };

    gtag('event', 'page_view', pageData);
    
    // Track specific page types
    this.trackPageType();
    this.sessionData.page_views++;
  }

  /**
   * Track specific page types with enhanced data
   */
  trackPageType() {
    const template = document.body.className.match(/template-(\w+)/);
    if (!template) return;

    const pageType = template[1];
    
    switch (pageType) {
      case 'product':
        this.trackProductPageView();
        break;
      case 'collection':
        this.trackCollectionPageView();
        break;
      case 'cart':
        this.trackCartPageView();
        break;
      case 'index':
        this.trackHomepageView();
        break;
    }
  }

  /**
   * Track product page views with detailed product data
   */
  trackProductPageView() {
    const productData = this.getProductData();
    if (!productData) return;

    gtag('event', 'view_item', {
      currency: 'USD',
      value: productData.price,
      items: [{
        item_id: productData.id,
        item_name: productData.title,
        item_category: productData.type,
        item_variant: productData.variant,
        price: productData.price,
        quantity: 1
      }]
    });

    // Custom product engagement event
    gtag('event', 'product_page_engagement', {
      product_id: productData.id,
      product_title: productData.title,
      product_price: productData.price,
      product_available: productData.available
    });
  }

  /**
   * Track collection page views
   */
  trackCollectionPageView() {
    const collectionData = this.getCollectionData();
    
    gtag('event', 'view_item_list', {
      item_list_id: collectionData.handle,
      item_list_name: collectionData.title,
      items: collectionData.products
    });

    gtag('event', 'collection_page_view', {
      collection_handle: collectionData.handle,
      collection_title: collectionData.title,
      products_count: collectionData.products.length
    });
  }

  /**
   * Track add to cart events with enhanced data
   */
  trackAddToCart() {
    document.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest('[name="add"], .btn-cart, .add-to-cart');
      if (!addToCartBtn) return;

      const productData = this.getProductDataFromForm(addToCartBtn);
      if (!productData) return;

      gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: productData.price * productData.quantity,
        items: [{
          item_id: productData.id,
          item_name: productData.title,
          item_category: productData.type,
          item_variant: productData.variant,
          price: productData.price,
          quantity: productData.quantity
        }]
      });

      // Custom add to cart engagement
      gtag('event', 'cart_interaction', {
        interaction_type: 'add_to_cart',
        product_id: productData.id,
        variant_id: productData.variant_id,
        quantity: productData.quantity,
        cart_value: this.getCartValue()
      });
    });
  }

  /**
   * Track remove from cart events
   */
  trackRemoveFromCart() {
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.cart-remove, [data-cart-remove]');
      if (!removeBtn) return;

      const itemData = this.getCartItemData(removeBtn);
      if (!itemData) return;

      gtag('event', 'remove_from_cart', {
        currency: 'USD',
        value: itemData.price * itemData.quantity,
        items: [{
          item_id: itemData.id,
          item_name: itemData.title,
          item_category: itemData.type,
          item_variant: itemData.variant,
          price: itemData.price,
          quantity: itemData.quantity
        }]
      });
    });
  }

  /**
   * Track search events with query analysis
   */
  trackSearch() {
    const searchForms = document.querySelectorAll('form[action*="search"]');
    
    searchForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        const query = form.querySelector('input[name="q"]')?.value;
        if (!query) return;

        gtag('event', 'search', {
          search_term: query,
          search_location: 'header'
        });

        // Enhanced search tracking
        gtag('event', 'site_search', {
          search_query: query,
          search_results_count: this.getSearchResultsCount(),
          search_refinements: this.getSearchFilters()
        });
      });
    });
  }

  /**
   * Track user engagement metrics
   */
  trackUserEngagement() {
    // Track clicks on key elements
    const engagementElements = [
      '.product-card',
      '.collection-card', 
      '.banner-link',
      '.newsletter-signup',
      '.social-link'
    ];

    engagementElements.forEach(selector => {
      document.addEventListener('click', (e) => {
        if (!e.target.closest(selector)) return;

        gtag('event', 'user_engagement', {
          engagement_type: selector.replace('.', ''),
          element_text: e.target.textContent?.trim().substring(0, 100),
          page_location: window.location.href
        });
      });
    });
  }

  /**
   * Track scroll depth for engagement analysis
   */
  trackScrollDepth() {
    let maxScroll = 0;
    const milestones = [25, 50, 75, 90];
    const tracked = new Set();

    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        this.sessionData.scroll_depth = maxScroll;
      }

      milestones.forEach(milestone => {
        if (scrollPercent >= milestone && !tracked.has(milestone)) {
          tracked.add(milestone);
          gtag('event', 'scroll_depth', {
            scroll_depth: milestone,
            page_location: window.location.href
          });
        }
      });
    });
  }

  /**
   * Track time spent on page
   */
  trackTimeOnPage() {
    const startTime = Date.now();
    
    // Track time milestones
    const timeMilestones = [30, 60, 120, 300]; // seconds
    const tracked = new Set();

    setInterval(() => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      this.sessionData.time_on_site = timeSpent;

      timeMilestones.forEach(milestone => {
        if (timeSpent >= milestone && !tracked.has(milestone)) {
          tracked.add(milestone);
          gtag('event', 'page_engagement_time', {
            engagement_time: milestone,
            page_location: window.location.href
          });
        }
      });
    }, 10000); // Check every 10 seconds
  }

  /**
   * Track form interactions (newsletter, contact, etc.)
   */
  trackFormInteractions() {
    const forms = document.querySelectorAll('form');

    forms.forEach(form => {
      const formType = this.getFormType(form);

      form.addEventListener('submit', (e) => {
        gtag('event', 'form_submission', {
          form_type: formType,
          form_location: this.getFormLocation(form)
        });
      });

      // Track form field interactions
      const inputs = form.querySelectorAll('input, textarea, select');
      inputs.forEach(input => {
        input.addEventListener('focus', () => {
          gtag('event', 'form_interaction', {
            interaction_type: 'field_focus',
            form_type: formType,
            field_type: input.type || input.tagName.toLowerCase()
          });
        });
      });
    });
  }

  /**
   * Track product views on product pages
   */
  trackProductViews() {
    if (document.body.classList.contains('template-product')) {
      const productData = this.getProductData();
      if (productData) {
        gtag('event', 'view_item', {
          currency: 'USD',
          value: productData.price,
          items: [{
            item_id: productData.id,
            item_name: productData.title,
            price: productData.price,
            quantity: 1
          }]
        });
      }
    }
  }

  /**
   * Track clicks on product cards/links
   */
  trackProductClicks() {
    const productLinks = document.querySelectorAll('.product-card a, .product-item a, a[href*="/products/"]');

    productLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const productTitle = link.querySelector('.product-title')?.textContent?.trim() ||
                           link.textContent?.trim();

        gtag('event', 'select_item', {
          item_list_name: 'Product Grid',
          items: [{
            item_name: productTitle,
            item_list_name: 'Product Grid'
          }]
        });
      });
    });
  }

  /**
   * Track cart view events
   */
  trackCartView() {
    if (document.body.classList.contains('template-cart')) {
      gtag('event', 'view_cart', {
        currency: 'USD',
        value: this.getCartValue()
      });
    }
  }

  /**
   * Track navigation menu interactions
   */
  trackNavigation() {
    const navLinks = document.querySelectorAll('nav a, .navigation a');

    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        gtag('event', 'navigation_click', {
          link_text: link.textContent?.trim(),
          link_url: link.href,
          navigation_type: link.closest('header') ? 'header' : 'footer'
        });
      });
    });
  }

  /**
   * Track checkout process steps
   */
  trackCheckoutSteps() {
    if (window.location.pathname.includes('/checkout')) {
      // Basic checkout tracking - can be enhanced based on specific checkout flow
      gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: this.getCartValue()
      });
    }
  }

  /**
   * Helper method to get search results count
   */
  getSearchResultsCount() {
    const resultsEl = document.querySelector('.search-results-count');
    if (resultsEl) {
      const count = parseInt(resultsEl.textContent.match(/\d+/)?.[0] || '0');
      return count;
    }
    return 0;
  }

  /**
   * Helper method to get search filters
   */
  getSearchFilters() {
    const filters = [];
    const activeFilters = document.querySelectorAll('.filter.active, .active-filter');
    activeFilters.forEach(filter => {
      filters.push(filter.textContent?.trim());
    });
    return filters.join(',');
  }

  /**
   * Helper method to get collection handle
   */
  getCollectionHandle() {
    const match = window.location.pathname.match(/\/collections\/([^\/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Helper method to get collection products
   */
  getCollectionProducts() {
    const products = [];
    const productCards = document.querySelectorAll('.product-card, .product-item');
    productCards.forEach((card, index) => {
      if (index < 10) { // Track first 10 products only
        const title = card.querySelector('.product-title')?.textContent?.trim();
        if (title) products.push(title);
      }
    });
    return products;
  }

  // Utility methods for data extraction

  /**
   * Get product data from current page or element
   */
  getProductData() {
    // Try to get from Shopify's product object
    if (typeof window.product !== 'undefined') {
      return {
        id: window.product.id,
        title: window.product.title,
        type: window.product.type,
        vendor: window.product.vendor,
        price: window.product.price / 100,
        available: window.product.available,
        variant: window.product.variants?.[0]?.title
      };
    }

    // Fallback to DOM extraction
    return this.extractProductDataFromDOM();
  }

  /**
   * Get product data from form or button element
   */
  getProductDataFromForm(element) {
    const form = element.closest('form');
    if (!form) return null;

    // Try to get data from form elements
    const productId = form.querySelector('[name="id"]')?.value ||
                     form.querySelector('[name="product-id"]')?.value;

    const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1');

    // Try to get price from various sources
    let price = 0;
    const priceElement = form.querySelector('.price, .product-price, [data-price]');
    if (priceElement) {
      const priceText = priceElement.dataset.price || priceElement.textContent;
      price = this.parsePrice(priceText);
    }

    // Try to get product title
    let title = '';
    const titleElement = form.closest('[data-product-title]') ||
                        form.closest('.product-card')?.querySelector('.product-title') ||
                        document.querySelector('.product-title, h1');
    if (titleElement) {
      title = titleElement.dataset.productTitle || titleElement.textContent?.trim();
    }

    // Return product data if we have at least an ID or title
    if (productId || title) {
      return {
        id: productId || 'unknown',
        title: title || 'Product',
        price: price,
        quantity: quantity,
        variant: form.querySelector('[name="variant"]')?.value || null
      };
    }

    // Fallback to current page product data
    return this.getProductData();
  }

  /**
   * Extract product data from DOM elements
   */
  extractProductDataFromDOM() {
    const titleEl = document.querySelector('.product-title, h1');
    const priceEl = document.querySelector('.price, .product-price');
    
    if (!titleEl || !priceEl) return null;

    return {
      id: this.extractProductId(),
      title: titleEl.textContent?.trim(),
      price: this.parsePrice(priceEl.textContent),
      available: !document.querySelector('.sold-out')
    };
  }

  /**
   * Get collection data from current page
   */
  getCollectionData() {
    return {
      handle: this.getCollectionHandle(),
      title: document.querySelector('.collection-title, h1')?.textContent?.trim(),
      products: this.getCollectionProducts()
    };
  }

  /**
   * Get cart value
   */
  getCartValue() {
    // Try Shopify cart object first
    if (typeof window.cart !== 'undefined') {
      return window.cart.total_price / 100;
    }
    
    // Fallback to DOM extraction
    const cartTotal = document.querySelector('.cart-total, .total-price');
    return cartTotal ? this.parsePrice(cartTotal.textContent) : 0;
  }

  /**
   * Get device type for segmentation
   */
  getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Get traffic source information
   */
  getTrafficSource() {
    const referrer = document.referrer;
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('utm_source')) {
      return urlParams.get('utm_source');
    }
    
    if (referrer.includes('google')) return 'google';
    if (referrer.includes('facebook')) return 'facebook';
    if (referrer.includes('instagram')) return 'instagram';
    if (referrer === '') return 'direct';
    
    return 'referral';
  }

  /**
   * Parse price from text content
   */
  parsePrice(priceText) {
    if (!priceText) return 0;
    const price = priceText.replace(/[^\d.,]/g, '');
    return parseFloat(price) || 0;
  }

  /**
   * Extract product ID from various sources
   */
  extractProductId() {
    // Try data attributes
    const productEl = document.querySelector('[data-product-id]');
    if (productEl) return productEl.dataset.productId;
    
    // Try URL pattern
    const urlMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
    return urlMatch ? urlMatch[1] : null;
  }

  /**
   * Get content group for page categorization
   */
  getContentGroup() {
    const template = document.body.className.match(/template-(\w+)/);
    return template ? template[1] : 'other';
  }

  /**
   * Get custom page data
   */
  getCustomPageData() {
    return {
      has_sale_items: !!document.querySelector('.sale, .on-sale'),
      has_new_items: !!document.querySelector('.new, .badge-new'),
      page_language: document.documentElement.lang || 'en'
    };
  }

  /**
   * Get form type from form element
   */
  getFormType(form) {
    if (form.classList.contains('newsletter')) return 'newsletter';
    if (form.classList.contains('contact')) return 'contact';
    if (form.action.includes('contact')) return 'contact';
    if (form.action.includes('search')) return 'search';
    return 'other';
  }

  /**
   * Get form location context
   */
  getFormLocation(form) {
    if (form.closest('footer')) return 'footer';
    if (form.closest('header')) return 'header';
    if (form.closest('.popup, .modal')) return 'popup';
    return 'main_content';
  }
}

// Initialize the tracker when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ga4Tracker = new GA4EcommerceTracker();
  });
} else {
  window.ga4Tracker = new GA4EcommerceTracker();
}

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GA4EcommerceTracker;
}
